
import { defineComponent, ref, onMounted, onUnmounted } from "vue"
import { useStore } from "vuex"
import { useRouter } from "vue-router"
import { ElMessage, ElMessageBox } from "element-plus"
import { Actions } from "@/store/enums/StoreEnums"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"

export default defineComponent({
  name: "mall-products-index",
  setup() {
    const store = useStore()
    const router = useRouter()
    // 按行记账（而非单个全局 ref），避免多行同时操作时互相抢占/提前解锁
    const savingRows = ref({} as Record<number, boolean>)
    // 保存失败时把该行回滚到改动前的值，避免界面数字与库里不一致
    const originalRows = ref({} as Record<number, any>)
    // el-input-number 的显示由它自己的私有状态（userInput/currentValue）决定，
    // 直接改 row[field] 拉不动这层私有状态；空值回滚时用它强制重挂载对齐显示
    const inputKeys = ref({} as Record<string, number>)

    const tableData = ref([] as Array<any>)
    const tableRef = ref()
    const product_brand_options = ref([] as Array<any>)
    const product_ip_options = ref([] as Array<any>)

    const defaultForm = () => ({
      name: "",
      product_brand: "",
      ip: "",
      is_presale: "",
      is_arrived: "",
      in_stock: "",
      sort: "",
    })

    const formData = ref(defaultForm())

    const page = ref({
      currentPage: 1,
      pageSize: 10,
      totalResult: 0,
    })

    const query = () => {
      store
        .dispatch(Actions.GET_MALL_PRODUCTS, { ...formData.value, ...page.value })
        .then(() => {
          // is_mall_top 归一化成真正的布尔值：历史数据存在为 NULL 的行时，el-switch 在
          // mounted 阶段发现 modelValue 不在 [true,false] 内会自己 emit 一次 change，
          // 命中 @change="onToggleTop(row)" 打一次没人点过的写库请求（后端 jbuilder 已经
          // `!!item.is_mall_top` 兜底，这里是前端侧的第二层防御）。
          // 注：这会让 tableData 里的元素变成新对象，但 query() 每次都是从 API 响应重新赋值
          // 整个数组，行对象跨请求本来就不保留引用（见下方 originalRows 清空的注释），
          // 现有的按 row.id 索引的 savingRows/originalRows/inputKeys 都不受影响。
          tableData.value = (store.getters.currentMallProducts || []).map((r) => ({
            ...r,
            is_mall_top: !!r.is_mall_top,
          }))
          page.value.totalResult = store.getters.currentMallProductCount
          // 行对象随刷新换新，旧缓存的“原值”会失真（跨翻页/跨用户改动），必须清空避免回滚写脏值
          originalRows.value = {}
        })
        .catch(() => {
          console.error("获取商城商品列表失败")
        })
    }

    const submitEvent = () => {
      page.value.currentPage = 1
      query()
    }

    const onPageChange = (e) => {
      page.value.currentPage = e.currentPage
      page.value.pageSize = e.pageSize
      query()
    }

    const onReset = () => {
      formData.value = defaultForm()
      page.value.currentPage = 1
      // 重置只清了 formData.sort，表头箭头是 vxe-table 自己的内部状态，不会跟着联动清掉，
      // 不调 clearSort 的话会出现“表头还停在上次排序的列，但数据其实已经是默认序”的显示与数据不一致
      if (tableRef.value && tableRef.value.clearSort) {
        tableRef.value.clearSort()
      }
      query()
    }

    // 排序走后端：前端排序只会排当前页，跨页顺序是错的
    // ⚠️ vxe-table 4.1.20-beta.1 的 sort-change 载荷里没有 field，只有 property/column，
    // 但它的 .d.ts 却声明了 field —— 类型对不代表运行时对，别改回解构 field
    const onSortChange = ({ column, order }) => {
      const sortField = column && column.field
      if (!order) {
        formData.value.sort = ""
      } else if (sortField === "mall_stock") {
        formData.value.sort = order === "asc" ? "stock_asc" : "stock_desc"
      } else if (sortField === "sold_count") {
        formData.value.sort = order === "asc" ? "sold_asc" : "sold_desc"
      } else {
        formData.value.sort = ""
      }
      page.value.currentPage = 1
      query()
    }

    const presaleModeText = (mode: string): string => {
      const map: Record<string, string> = {
        full_only: "全款",
        deposit_only: "仅订金",
        deposit_or_full: "订金或全款",
      }
      return map[mode] || "-"
    }

    const depositText = (row: any): string => {
      if (row.deposit_type === "percent") {
        return `订金 ${row.deposit_percent}%`
      }
      return `订金 ${row.deposit_amount}`
    }

    // 行内编辑的「原值」快照。字段集在 cacheOriginal / doSave 成功回调 / onToggleTop 三处都要用，
    // 抽成一处避免加字段时漏改导致回滚丢字段
    const snapshotOf = (row: any) => ({
      mall_stock: row.mall_stock,
      purchase_limit: row.purchase_limit,
      is_mall_top: row.is_mall_top,
      water_need: row.water_need,
      daily_purchase_limit: row.daily_purchase_limit,
    })

    const cacheOriginal = (row: any) => {
      if (!originalRows.value[row.id]) {
        originalRows.value[row.id] = snapshotOf(row)
      }
    }

    const rollback = (row: any, field: string) => {
      const original = originalRows.value[row.id]
      if (original && original[field] !== undefined) {
        row[field] = original[field]
      }
    }

    // 强制重挂载 el-input-number 用：回滚只改了 row[field]，拉不动组件私有的 userInput/currentValue，
    // 靠 :key 变化触发组件重建，让它的私有状态重新从 modelValue 初始化
    const bumpInputKey = (row: any, field: string) => {
      const k = `${row.id}:${field}`
      inputKeys.value[k] = (inputKeys.value[k] || 0) + 1
    }

    const inputKeyOf = (row: any, field: string) => {
      return `${row.id}:${field}:${inputKeys.value[`${row.id}:${field}`] || 0}`
    }

    // 复用既有 products#update，只传要改的那个字段
    const doSave = (row: any, field: string) => {
      // 400ms 防抖窗口内，el-input-number 的 handleInput 可能不发 change 就把值置成 null
      // （saveField 排期时的守卫查不到这种情况），必须在真正组装请求体前现查一次
      if (row[field] === null || row[field] === undefined) {
        rollback(row, field)
        bumpInputKey(row, field)
        ElMessage.warning("值不能为空，已恢复原值")
        return
      }

      savingRows.value[row.id] = true

      const values = {}
      values[field] = row[field]

      store
        .dispatch(Actions.UPDATE_PRODUCT, { id: row.id, values: values })
        .then(() => {
          originalRows.value[row.id] = snapshotOf(row)
          ElMessage.success("已保存")
        })
        .catch(() => {
          rollback(row, field)
          ElMessage.error("保存失败，已恢复原值")
        })
        .finally(() => {
          delete savingRows.value[row.id]
        })
    }

    // 方向键连按会连续触发 change；按“行+字段”分别防抖，避免中间值真实落库
    // （且不能用单个全局 timer，否则改 A 行会取消 B 行正在等待的保存）
    const saveTimers = {} as Record<string, any>

    const saveField = (row: any, field: string) => {
      // 这里是“排期时”的早拦截，能给用户更快的反馈；但防抖窗口内值还可能变成 null（见 doSave 里的现查注释），
      // 所以这道守卫不能替代 doSave 里那道，且必须在 cacheOriginal 之前判断——否则会把 null 当成“原值”缓存进去
      if (row[field] === null || row[field] === undefined) {
        rollback(row, field)
        bumpInputKey(row, field)
        ElMessage.warning("值不能为空，已恢复原值")
        return
      }

      cacheOriginal(row)

      const key = `${row.id}:${field}`
      if (saveTimers[key]) {
        clearTimeout(saveTimers[key])
      }
      saveTimers[key] = setTimeout(() => {
        delete saveTimers[key]
        doSave(row, field)
      }, 400)
    }

    // el-switch 的 change 触发时 v-model 已经翻转成新值，必须先手动缓存翻转前的值再保存；
    // 开关没有连续中间态，不需要走 saveField 的防抖，直接保存
    const onToggleTop = (row: any) => {
      if (!originalRows.value[row.id]) {
        originalRows.value[row.id] = {
          ...snapshotOf(row),
          is_mall_top: !row.is_mall_top, // change 触发时 v-model 已翻转，原值是取反
        }
      }
      doSave(row, "is_mall_top")
    }

    // 标记到货会真实向已下订金的用户发出补款通知，且不可撤销
    const onMarkArrived = (row: any) => {
      ElMessageBox.confirm(
        `确定将「${row.name}」标记为已到货？系统会立即向已付订金的用户发送补款通知，此操作不可撤销。`,
        "标记到货",
        { confirmButtonText: "确定标记", cancelButtonText: "取消", type: "warning" }
      )
        .then(() => {
          savingRows.value[row.id] = true
          return store.dispatch(Actions.MARK_PRODUCT_ARRIVED, row.id)
        })
        .then(() => {
          ElMessage.success("已标记到货，相关订单已发送补款通知")
          query()
        })
        .catch((err) => {
          if (err !== "cancel" && err !== "close") {
            ElMessage.error((err && err.message) || "标记到货失败")
          }
        })
        .finally(() => {
          delete savingRows.value[row.id]
        })
    }

    const onNew = () => {
      router.push({ path: "/products/new", query: { is_mall_product: "true" } })
    }

    onMounted(() => {
      setCurrentPageBreadcrumbs("商城商品", ["商城管理"])
    })

    // 组件卸载（如点「编辑」跳转）时清掉所有未触发的防抖 timer，
    // 避免离开页面后 PATCH 仍在后台发出，甚至晚于编辑页的保存把刚写的值覆盖回去
    onUnmounted(() => {
      Object.keys(saveTimers).forEach((k) => clearTimeout(saveTimers[k]))
    })

    store.dispatch(Actions.GET_PRODUCT_BRAND_OPTIONS).then(() => {
      product_brand_options.value = store.getters.currentProductBrandOptions
    })

    store.dispatch(Actions.GET_PRODUCT_IP_OPTIONS).then(() => {
      product_ip_options.value = store.getters.currentProductIpOptions
    })

    query()

    return {
      tableRef,
      tableData,
      formData,
      page,
      product_brand_options,
      product_ip_options,
      savingRows,
      inputKeys,
      inputKeyOf,
      query,
      submitEvent,
      onPageChange,
      onReset,
      onSortChange,
      presaleModeText,
      depositText,
      cacheOriginal,
      saveField,
      onToggleTop,
      onMarkArrived,
      onNew,
    }
  },
})
